// @ts-nocheck

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { KeyboardClear, KeyboardReturn, Minus, Plus } from '../../../../assets/svg';
import KeyboardTransitionGroup from '../../../../components/KeyboardTransitionGroup';
import { Overlay } from '../../../../components/Modal/Modal.style';
import { getPortalRootDOMElement } from '../../../../DOMElements';
import usePrevious from '../../../../hooks/usePrevious';
import { __, getDecimalSeparator } from '../../../../i18n/l10n';
import FontSize from '../../../../renderprops/FontSize';

import FastButton from './GridsFastButton';

const Wrapper = styled.div`
  height: 2em;
`;

const Input = styled.div`
  && {
    line-height: 2em;
    color: ${({ $isValid }) => ($isValid ? '#b5121b' : '#ddd')};
    font-weight: 600;
    margin: 0 0.2em;
    width: 4em;
    height: 2em;
    padding: 0 0.4em;
    text-align: right;
    float: left;
  }
`;

const InputValue = styled.div`
  display: inline-block;
  width: 100%;
  overflow: hidden;
`;

const StyledButton = styled.button`
  && {
    width: 2em;
    height: 2em;
    padding: 0;
    font-weight: 600;
    float: left;
    background: ${({ theme }) => theme.grids.colors.darkgrey4};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    box-shadow: none;

    &:active {
      transform: scale(0.9);
    }
  }
`;

const commonPicto = css`
  color: #fff;
  width: 1.4em;
  height: 1.4em;
  pointer-events: none;
`;

const MinusIcon = styled(Minus)`
  ${commonPicto}
`;

const PlusIcon = styled(Plus)`
  ${commonPicto}
`;

const Keyboard = styled.div`
  position: absolute;
  bottom: 0;
  padding-bottom: 0;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  left: 0;
  background: #f2f2f2;
  z-index: 200;
  width: 100%;
  box-shadow: 1px 0 1px #ddd;
`;

const Key = styled(FastButton)`
  && {
    width: 100%;
    height: 100%;
    padding: 0;
    font-size: 1.2em;
    box-shadow: none;
    background: #ddd;
    font-weight: 600;
    border-radius: 2px;
    cursor: pointer;

    /* Prevent the callout on tap-hold and text selection */
    user-select: none;
  }
`;

const KeyWrapper = styled.div`
  width: 33.33%;
  height: 3.5em;
  padding: 0.1em;
  box-sizing: border-box;
  float: left;
`;

const Keys = styled.div`
  background-color: #f2f2f2;
  padding: 0.1em;
  ::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const Amount = styled.div`
  position: relative;
  line-height: 3.5em;
  height: 3.5em;
  padding-right: 3.5em;
  text-align: right;
  background-color: #fff;
`;

const AmountValue = styled.span`
  font-size: 1.6em;
  color: #333;

  /* Prevent the callout on tap-hold and text selection */
  user-select: none;
`;

const ClearButton = styled(FastButton)`
  && {
    position: absolute;
    right: 0;
    top: 0;
    width: 3em;
    height: 3.5em;
    padding: 0;
    line-height: 0;
    text-align: center;
    > svg {
      width: 1.4em;
      height: 1.4em;
      use {
        fill: #333;
      }
    }
  }
`;

const KeyboardReturnIcon = styled(KeyboardReturn)`
  width: 1.2em;
  height: 1.2em;
  color: ${({ theme }) => theme.common.colors.primary};
`;

const KeyboardClearIcon = styled(KeyboardClear)`
  width: 1.2em;
  height: 1.2em;
`;

// Older browsers don't support event options, feature detect it.
let hasPassiveEvents = false;
const passiveTestOptions = {
  get passive() {
    hasPassiveEvents = true;
    return undefined;
  },
};
window.addEventListener('testPassive', null, passiveTestOptions);
window.removeEventListener('testPassive', null, passiveTestOptions);

type StakeInputProps = {
  onChange: (stake: string) => void;
  onClickMinus: () => void;
  onClickPlus: () => void;
  readOnly?: boolean;
  stake: string;
  isKeyboarDisabled?: boolean;
};

const StakeInput = ({
  onChange,
  onClickMinus,
  onClickPlus,
  readOnly,
  stake,
  isKeyboarDisabled = false,
}: StakeInputProps) => {
  const ref = useRef(null);
  const keyboardRef = useRef(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [value, setValue] = useState('');
  const previousShowKeyboard = usePrevious(showKeyboard);
  const previousValue = usePrevious(value);

  const closeKeyboard = useCallback(() => {
    setShowKeyboard(false);
  }, []);

  const handleOutsideClick = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      if (keyboardRef.current && !keyboardRef.current.contains(e.target) && showKeyboard) {
        closeKeyboard();
      }
    },
    [closeKeyboard, showKeyboard],
  );

  const openKeyboard = useCallback(() => {
    setShowKeyboard(true);
    setValue(null);
  }, []);

  const onClick = useCallback(
    (e) => {
      if (!isKeyboarDisabled) {
        e.stopPropagation();
        openKeyboard();
      }
    },
    [isKeyboarDisabled, openKeyboard],
  );

  useEffect(() => {
    const passiveOptions = hasPassiveEvents ? { passive: false } : undefined;
    if (showKeyboard) {
      document.addEventListener('touchstart', handleOutsideClick, passiveOptions);
    }
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick, passiveOptions);
    };
  }, [handleOutsideClick, showKeyboard]);

  useEffect(() => {
    const domElem = ref.current;
    if (domElem) {
      domElem.addEventListener('click', onClick);
    }
    return () => {
      if (domElem) {
        domElem.removeEventListener('click', onClick);
      }
    };
  }, [onClick]);

  useEffect(() => {
    if (previousShowKeyboard === showKeyboard && previousValue !== value) {
      onChange(value);
    }
  }, [onChange, previousShowKeyboard, previousValue, showKeyboard, value]);

  const onKeyPress = useCallback(
    (key) => () => {
      if (key === 'Enter') {
        closeKeyboard();
      } else {
        setValue((oldValue) =>
          oldValue ? `${oldValue}${key}`.replace(/(,|\.)+/, '.') : String(key).replace(/(,|\.)+/, '.'),
        );
      }
    },
    [closeKeyboard],
  );

  const onClear = useCallback(() => setValue(''), []);

  return (
    <Wrapper>
      <StyledButton onClick={readOnly ? null : onClickMinus}>
        <MinusIcon />
      </StyledButton>
      <Input $isValid={!!stake} ref={ref} readOnly onChange={onChange}>
        <InputValue>{stake ? stake.replace('.', getDecimalSeparator()) : __('Wager')}</InputValue>
      </Input>
      <StyledButton onClick={readOnly ? null : onClickPlus}>
        <PlusIcon />
      </StyledButton>
      {document.body &&
        createPortal(
          <FontSize>
            {({ fontSize }) => (
              <div style={{ fontSize }}>
                <>
                  {showKeyboard ? <Overlay $zIndex={190} /> : null}
                  <KeyboardTransitionGroup>
                    {showKeyboard ? (
                      <Keyboard ref={keyboardRef}>
                        <Amount>
                          <AmountValue>{stake ? stake.replace('.', getDecimalSeparator()) : ''}</AmountValue>
                          <ClearButton onClick={onClear}>
                            <KeyboardClearIcon />
                          </ClearButton>
                        </Amount>
                        <Keys>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, getDecimalSeparator(), 0, 'Enter'].map((key) => (
                            <KeyWrapper key={key}>
                              <Key onClick={onKeyPress(key)}>{key === 'Enter' ? <KeyboardReturnIcon /> : key}</Key>
                            </KeyWrapper>
                          ))}
                        </Keys>
                      </Keyboard>
                    ) : null}
                  </KeyboardTransitionGroup>
                </>
              </div>
            )}
          </FontSize>,
          getPortalRootDOMElement(),
        )}
    </Wrapper>
  );
};

export default StakeInput;
